/**
 * useSanctaSound — soft, non-intrusive chime played when Sancta delivers a
 * result (reply finished / products appeared). Pure Web Audio (no asset
 * download), respects a persisted mute toggle, and never plays during idle
 * or sleep. One shared AudioContext across the app.
 */
import { useCallback, useRef, useState } from "react";

const MUTE_KEY = "sb_sancta_muted_v1";

let _ctx: AudioContext | null = null;
function _audioCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!_ctx) _ctx = new Ctor();
    return _ctx;
  } catch { return null; }
}

export function useSanctaSound() {
  const [muted, setMuted] = useState<boolean>(() => {
    try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
  });
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  /** Play a gentle two-note "results ready" chime (E6 → A6). */
  const chime = useCallback(() => {
    if (mutedRef.current) return;
    const ctx = _audioCtx();
    if (!ctx) return;
    try {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const master = ctx.createGain();
      master.gain.value = 0.0001;
      master.connect(ctx.destination);
      const notes = [1318.5, 1760.0]; // E6, A6
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const t = now + i * 0.09;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.09, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
        osc.connect(g);
        g.connect(master);
        master.gain.setValueAtTime(0.9, t);
        osc.start(t);
        osc.stop(t + 0.35);
      });
    } catch { /* silent — sound never breaks UX */ }
  }, []);

  return { muted, toggleMute, chime };
}

export default useSanctaSound;
